import React, { useEffect, useState } from "react";

import sendIcon from "../../src/img/send-icon.svg";

import LoadingButton from "@mui/lab/LoadingButton";

import { isBlank, copyToClipBoard } from "../utils";
import { useReSendVerificationEmailMutation } from "../services/accountsApi";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';


import { STATES } from "../constants";
import BasicModalConfirmation from "./basicModalConfirmation";
import { Button, Checkbox, FormControlLabel, FormGroup, Box, IconButton, CircularProgress, Input, Avatar } from "@mui/material";
import { parseToEasternTime } from "../utils/dates";
import { useSelector } from 'react-redux';
import { selectAllUserTypes } from './../store/slices/accounts/userTypes';
import { UserType } from "../constants/enums";
import { discordSchema } from "../validations/discordValidation";
import { paymentsEmailSchema } from "../validations/paymentsEmailValidation";
import { stnIdSchema  } from "../validations/stnIdValidation";
import VerifiedIcon from '@mui/icons-material/Verified';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import SyncIcon from '@mui/icons-material/Sync';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import useBuyManager from "../customHooks/useBuyManager";
import ColorPicker from "./colorPicker/ColorPicker";
import useUserTypes from "../customHooks/useUserTypes";

const ERR_MSG = {
  psw_required: "The Password is requrired",
  psw_min_length: "Password min length is six",
  psw_are_equals: "Password are not equals",
};

function CopyIconComponent({text}) {
  return (
    <IconButton
      color="primary"
      size="small"
      onClick={() => copyToClipBoard(text)}
      disabled={!text}
    >
      <ContentCopyIcon fontSize="small" />
    </IconButton>
  );
}

export default function EditAcountsModal({
  user,
  closeModal,
  onSubmit,
  onDelete,
  onRestore,
  reSendVerificationEmailResult,
}) {

  const [initReSendVerificationEmail, {isLoading: reSendVerificationEmailsLoading}] = useReSendVerificationEmailMutation();

  let creatingNewUser = typeof user == "boolean";

  if (creatingNewUser) {
    user = {};
  }

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showRestoreConfirmationModal, setShowRestoreConfirmationModal] = useState(false);
  
  const confirmDelete = () => {
    setShowConfirmationModal(true);
  };

  const userTypes = useUserTypes();
  const [formIsDisabled, setFormIsDisabled] = useState(true); //A: default is disabled

  const [firstName, setFirstName] = useState(user.firstName || "");
  const [lastName, setLastName] = useState(user.lastName || "");
  const [email, setEmail] = useState(user.email || "");
  const [screenName, setScreenName] = useState(user.screenName || "");
  const [address1, setAddress1] = useState(user.address1);
  const [address2, setAddress2] = useState(user.address2);
  const [city, setCity] = useState(user.city);
  const [state, setState] = useState(user.state);
  const [zip, setZip] = useState(user.zip);
  const [mobilePhone, setMobilePhone] = useState(user.mobilePhone);
  const [status, setStatus] = useState(user.status);
  const [userType, setUserType] = useState(user.userTypeId || UserType.NewBee); //A: default userType
  const [role, setRole] = useState(user.role || "User");//A: default role
  const [userDistribution, setUserDistribution] = useState(
    user.userDistribution
  );
  const [doNotDroneBuy, setDoNotDroneBuy] = useState(user.doNotDroneBuy);
  const [isFetching, setIsFetching] = useState(false);
  const [useSwarm, setUseSwarm] = useState(
    user.useSwarm
  );
  const [discordTag, setDiscordTag] = useState(user.discordTag || "")
  const [paymentsEmail, setPaymentsEmail] = useState(user.paymentsEmail || "")
  const [StnId, setStnId] = useState(user.stnId);
  const [isFetchingStnId, setIsFetchingStnId] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [
    reSendVerificationEmailInProgress,
    setReSendVerificationEmailInProgress,
  ] = useState(false);

  const [errors, setErrors] = useState([]);

  const [password, setPassword] = useState(null);
  const [confirmPassword, setConfirmPassword] = useState(null);

  const {buyManager, getBuyManagerByUserId, updateBuyManagerData, updatedBM} = useBuyManager(user.id);

  useEffect(()=>{if(user?.id)getBuyManagerByUserId(user.id)}, [user]);

  async function reSenVerificationEmailOnClick() {
    try {
      setReSendVerificationEmailInProgress(true);
      await initReSendVerificationEmail(user.id).unwrap();
      setReSendVerificationEmailInProgress(false);
      reSendVerificationEmailResult(true);
    } catch (error) {
      setReSendVerificationEmailInProgress(false);
      console.log(
        "error reSendVerificationEmail for userId: " +
        user.id +
        " error: " +
        error
      );
      reSendVerificationEmailResult(false);
    }
  }

  function errorMessageExists(errorMsg) {
    return errors.find((err) => {
      if (err.includes(errorMsg)) {
        return true;
      }
    });
  }

  function cleanErrorMessage(errMsgKey) {
    let errorMsgValue = ERR_MSG[errMsgKey];
    var array = [...errors]; // make a separate copy of the array
    var index = array.indexOf(errorMsgValue);
    if (index !== -1) {
      array.splice(index, 1);
      setErrors(array);
    }
  }
  function cleanAllErrorsMessages() {
    setErrors([]);
  }

  function addErrorMessage(key) {
    let str = ERR_MSG[key];
    if (errorMessageExists(str)) return;
    setErrors((prevErrors) => [...prevErrors, str]);
  }

  function addErrorMessageFromBackEnd(str) {
    setErrors((prevErrors) => [...prevErrors, str]);
  }

  function validatePasswordsAreEquals(password, confirmPassword) {
    if (password !== confirmPassword) {
      addErrorMessage("psw_are_equals");
      return false;
    }
    cleanErrorMessage("psw_are_equals");
    return true;
  }

  function validatePasswordMinLength(password, confirmPassword) {
    if (isBlank(password) && isBlank(confirmPassword)) return true;

    let hasMinLength = password.length >= 6;
    if (!hasMinLength) addErrorMessage("psw_min_length");
    if (hasMinLength) {
      cleanErrorMessage("psw_min_length");
    }
    return hasMinLength;
  }

  function passwordExist(password) {
    if (password == null || isBlank(password)) {
      addErrorMessage("psw_required");
      return false;
    } else {
      cleanErrorMessage(ERR_MSG["psw_required"]);
      return true;
    }
  }

  function validatePassword(passwordIsRequired) {
    let password_clean = (password || "").trim();
    let confirmPassword_clean = (confirmPassword || "").trim();

    if (passwordIsRequired && !passwordExist(password_clean)) {
      return false;
    }
    const passwordAreEquals = validatePasswordsAreEquals(
      password_clean,
      confirmPassword_clean
    );
    const confirmMinLength = validatePasswordMinLength(
      password_clean,
      confirmPassword_clean
    );
    return passwordAreEquals && confirmMinLength;
  }

  async function validateDiscord() {
    // allow empty discord tag
    if(discordTag.length === 0) return true

    const isValid = await discordSchema.isValid({ discordUserName: discordTag });
    console.log(`validateDiscord: isValid = ${isValid}`);

    if (!isValid) {
      const errorMessage = "Discord User Name is invalid";
      console.log(`validateDiscord: errorMessage = ${errorMessage}`);
      setErrors((prevErrors) => [...prevErrors, errorMessage]);
    }

    return isValid;
  }

  async function validatePaypalEmail(){
    if (email && email !== "") {
      let emailIsValid = await paymentsEmailSchema.isValid(paymentsEmail);
      if (!emailIsValid) {
        setErrors((prevErrors) => [...prevErrors, "PayPal Email is invalid"]);
      }
      return emailIsValid;
    }
  }

  async function validateStnId() {
    if (StnId.trim() === "") {
      const errorMessage = "StnId is required";
      setErrors((prevErrors) => [...prevErrors, errorMessage]);
      return false;
    }
    const isValid = await stnIdSchema.isValid({ StnId });

    if (!isValid) {
      const errorMessage = "StnId must be a number";
      setErrors((prevErrors) => [...prevErrors, errorMessage]);
      return false;
    }

    return true;
  }
  
  function userBuilder() {
    return {
      email,
      firstName,
      lastName,
      screenName,
      address1,
      address2,
      city,
      state,
      zip,
      mobilePhone,
      status,
      password,
      confirmPassword,
      role,
      userDistribution,
      useSwarm,
      paymentsEmail,
      discordTag,
      "userTypeId": userType,
      StnId,
      doNotDroneBuy
    };
  }

  async function initNewUserProcess() {
    cleanAllErrorsMessages();

    let newUser = userBuilder();
    newUser.role = newUser.role || "User";
    let errors = await onSubmit(null, newUser, true);

    if (!errors) return;
    for (const [propertyName, errorMessages] of Object.entries(errors.errors)) {
      console.log(errorMessages[0]);
      let msg = errorMessages[0];
      if (
        msg ==
        "The field Password must be a string or array type with a minimum length of '6'."
      )
        msg = "Password min length is six";
      addErrorMessageFromBackEnd(msg);
    }
    setIsFetching(false);
  }

  async function editUserProcess() {
    let passwordIsOk = validatePassword();

    if (!passwordIsOk) return;

    let userEdited = userBuilder();
    await onSubmit(user.id, userEdited);
  }

  async function updateStnId() {
    setErrors([]);
    const isStnIdValid = await validateStnId(); // Validate StnId
    if (!isStnIdValid) return;
    
    setIsFetchingStnId(true);
    setUpdateSuccess(false);
    
    try {
      await onSubmit(user.id, { stnId: StnId });
      setUpdateSuccess(true);
    } catch (error) {
      console.error("Error updating STN ID:", error);
      setErrors((prevErrors) => [...prevErrors, "Failed to update StnId"]);
    } finally {
      setIsFetchingStnId(false);
    }
  }

  async function onLocalSubmit() {
    if( !(await validateDiscord())) return
    if( !(await validatePaypalEmail())) return

    setIsFetching(true);
    if (creatingNewUser) return initNewUserProcess();
    await editUserProcess();
    setIsFetching(false);
  }

  function handleRoleChange() {
    role === "Admin" ? setRole("User") : setRole("Admin");
  }

  function handleUserTypeChange(event) {
    setUserType(event.target.value);

    if (Number(event.target.value) === UserType.HiveAdmin) {
      setRole("Admin");
      setUserDistribution(false);
      setUseSwarm(false);
    }
  }
  return (
    <div>
      <div className="title-id">
        <h4 style={{ fontWeight: "600", color: "var(--orange)", margin: "0" }}>
          {creatingNewUser ? "Create Account" : "Edit Account"}
        </h4>
        <p style={{ fontWeight: "600",  margin: "0" }}>
          STN User ID for Buy Managers:
        </p>
        <div className="input-column" style={{margin:"0"}}>
          <input
            value={StnId}
            variant="contained"
            onChange={(e) => setStnId(e.target.value)}
            style={{ borderRadius: "0.5em", padding: "0.5em 0.75em", gap: "0.625em", width: "3.75em", height: "1.5em", margin: "0"}}
            />
          </div>
        <IconButton 
        onClick={() => {
          updateStnId()
        }}
        disabled= {isFetchingStnId}
        sx={{backgroundColor: updateSuccess ? "green" : "var(--orange)", height:"24px", width:"24px"}}
        >
          {
          isFetchingStnId
            ? <CircularProgress />
            : updateSuccess
              ? <CheckCircleIcon sx={{ color: "white", fontSize: "20px" }} />
              : <SyncIcon sx={{ color: "white", fontSize: "20px" }} />
        }
        </IconButton>
        {
          (updatedBM || buyManager) && <>             
            <Avatar sx={{ backgroundColor: (updatedBM || buyManager)?.color || '#FFB74D', height:"30px", width:"30px", fontSize:"12px", fontWeight:"700" }}>
              {Array.from((updatedBM || buyManager)?.firstName[0]) + Array.from((updatedBM || buyManager)?.lastName)[0]}
            </Avatar> 
            <ColorPicker onConfirmation={(newColor)=>updateBuyManagerData({ ...buyManager, color:newColor})}/>
            </>
        }
        
      </div>
      <BasicModalConfirmation
        title={`Are you sure you want to delete ${user.firstName} ${user.lastName} user?`}
        confirmLabel={"Yes"}
        cancelLabel={"No"}
        open={showConfirmationModal}
        onClick={() => {
          onDelete(user.id);
        }}
        handleClose={() => setShowConfirmationModal(false)}
      />

      <BasicModalConfirmation
        title={`Are you sure you want to restore ${user.firstName} ${user.lastName} user?`}
        confirmLabel={"Yes"}
        cancelLabel={"No"}
        open={showRestoreConfirmationModal}
        onClick={() => {
          onRestore(user.id);
        }}
        handleClose={() => setShowRestoreConfirmationModal(false)}
      />

      <div className="row-modal">
        <div className="column-modal">
          <div className="input-column">
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <label style={{ marginRight: '8px' }}>First Name</label>
              <CopyIconComponent text={firstName} />
            </div>
            <input
              label="First Name"
              disabled={formIsDisabled}
              name="firstName"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </div>

          <div className="input-column">
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <label>Last Name</label>
              <CopyIconComponent text={lastName} />
            </div>
            <input
              label="Last Name"
              name="lastName"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              disabled={formIsDisabled}
            />
          </div>

          <div className="input-column">
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <label>Email</label>
              <CopyIconComponent text={email} />
            </div>
            <input
              label="Email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={formIsDisabled}
            />
          </div>

          <div className="input-column">
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <label>Screen Name</label>
              <CopyIconComponent text={screenName} />
            </div>
            <input
              label="Screen Name"
              name="screenName"
              value={screenName}
              onChange={(e) => setScreenName(e.target.value)}
              disabled={formIsDisabled}
            />
          </div>

          <div className="input-column">
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <label>Discord User Name</label>
              <CopyIconComponent text={discordTag} />
            </div>
            <input
              label="Discord User Name"
              name="discordTag"
              value={discordTag}
              onChange={(e) => setDiscordTag(e.target.value)}
              disabled={formIsDisabled}
            />
          </div>

        </div>
        {/* ------------------------------ */}

        <div className="column-modal">
          <div className="input-column">
            <label>Change Password</label>
            <input
              type="password"
              label="Change Password"
              name="changePassword"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={formIsDisabled}
            />
          </div>

          <div className="input-column">
            <label>Confirm Password</label>
            <input
              type="password"
              label="Confirm Password"
              name="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={formIsDisabled}
            />
          </div>

          <div className="input-column">
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <label>Address 1</label>
              <CopyIconComponent text={address1} />
            </div>
            <input
              label="Address 1"
              name="address1"
              value={address1}
              onChange={(e) => setAddress1(e.target.value)}
              disabled={formIsDisabled}
            />
          </div>

          <div className="input-column">
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <label>Address 2</label>
              <CopyIconComponent text={address2} />
            </div>
            <input
              label="Address 2"
              name="address2"
              value={address2}
              onChange={(e) => setAddress2(e.target.value)}
              disabled={formIsDisabled}
            />
          </div>

          <div className="input-column">
            <Box sx={{display: 'flex'}}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <label>PayPal Email</label>
                <CopyIconComponent text={paymentsEmail} />
              </div>
              {user.paymentsEmail &&
                <Box className="status-data-card-profile">
                  {
                    user?.paymentsEmailVerified ?
                      <VerifiedIcon color='success' sx={{width: '0.8em', height: '0.8em'}} />
                      :
                      <NewReleasesIcon color='error' sx={{width: '0.8em', height: '0.8em'}}  />
                  }
                </Box>
              }
            </Box>
            <input
              label="PayPal Email"
              name="paypalEmail"
              value={paymentsEmail}
              onChange={(e) => setPaymentsEmail(e.target.value)}
              disabled={formIsDisabled}
            />
          </div>

        </div>
        {/* ------------------------------ */}
        <div className="column-modal">
          <div className="input-column">
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <label>City</label>
              <CopyIconComponent text={city} />
            </div>
            <input
              label="City"
              name="city"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              disabled={formIsDisabled}
            />
          </div>

          <div className="input-column">
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <label>State</label>
              <CopyIconComponent text={state} />
            </div>
            <select
              label="State"
              name="state"
              value={state || "default" }
              onChange={(e) => setState(e.target.value)}
              disabled={formIsDisabled}
            >
              {!state && (
                <option value="default">
                  {" "}
                  -- select an option --{" "}
                </option>
              )}
              {STATES.map((oneState) => (
                <option key={oneState.name} value={oneState.name}>{oneState.name}</option>
              ))}
            </select>
          </div>

          <div className="input-column">
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <label>ZIP</label>
              <CopyIconComponent text={zip} />
            </div>
            <input
              label="ZIP"
              name="zip"
              value={zip}
              onChange={(e) => setZip(e.target.value)}
              disabled={formIsDisabled}
            />
          </div>

          <div className="input-column">
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <label>Mobile Phone</label>
              <CopyIconComponent text={mobilePhone} />
            </div>
            <input
              label="Mobile Phone"
              name="mobilePhone"
              value={mobilePhone}
              onChange={(e) => setMobilePhone(e.target.value)}
              disabled={formIsDisabled}
            />
          </div>

        </div>        

        <div
          className="column-modal"
          style={{ width: "fit-content", margin: "0" }}
        >
          <div className="input-column">
            <label>
              <h4 style={{ color: "var(--orange)" }}>Status Account</h4>
            </label>
            <select
              label="State"
              name="status"
              value={status || "Verified"}
              onChange={(e) => setStatus(e.target.value)}
              disabled={formIsDisabled}
            >
              <option value="Unverified">Unverified</option>
              <option value="Verified">
                Verified
              </option>
              <option value="Active">Active</option>
            </select>
          </div>

          {reSendVerificationEmailInProgress ? (
            <LoadingButton
              loading
              variant="outlined"
              sx={{
                margin: "0 0 1em 0",
                height: "35px",
                width: "200px",
                border: "2px solid var(--orange)",
                borderRadius: "8px",
                color: "primary",
              }}
            >
              Submit
            </LoadingButton>
          ) : (
            <Button
              variant="outlined"
              style={{ margin: "0 0 1em 0" }}
              onClick={reSenVerificationEmailOnClick}
            >
              Send Email Verification
              <img
                src={sendIcon}
                alt="send icon"
                height="18px"
                width="18px"
                style={{ margin: "0 0 0 0.5em" }}
              />
            </Button>
          )}

          <div className="input-column">
            <label>
              <h4 style={{ color: "var(--orange)" }}> User Type</h4>
            </label>
            <select
              value={userType}
              onChange={handleUserTypeChange}
              disabled={formIsDisabled}
            >
              {
                userTypes?.length > 0 ?
                  userTypes.map((type) => {
                    return (
                      <option
                        key={type.id}
                        value={type.id}
                      >
                        {type.name}
                      </option>
                    );
                  })
                :
                  <option>Loading...</option>
              }
            </select>
          </div>

          <div className="checkbox-200px" style={{ margin: "0" }}>
            <Checkbox
              checked={userDistribution}
              onChange={() => setUserDistribution(!userDistribution)}
              id="userDistribution"
              name="User is eligible for distribution"
              disabled={formIsDisabled}
            />
            <label htmlFor="userDistribution">
              <p>User is eligible for distribution</p>
            </label>
          </div>

          <div className="checkbox-200px" style={{ margin: "0" }}>
            <Checkbox
              checked={useSwarm}
              onChange={() => setUseSwarm(!useSwarm)}
              id="useSwarm"
              name="User is eligible for Swarms"
              disabled={formIsDisabled}
            />
            <label htmlFor="useSwarm">
              <p>User is eligible for Swarms</p>
            </label>
          </div>

          <div className="checkbox-200px" style={{ margin: "0" }}>
            <Checkbox
              checked={doNotDroneBuy}
              onChange={() => setDoNotDroneBuy(!doNotDroneBuy)}
              id="doNotDroneBuy"
              name="User not is eligible for Drone Buy"
              disabled={formIsDisabled}
            />
            <label htmlFor="doNotDroneBuy">
              <p>Do Not Drone Buy</p>
            </label>
          </div>

          <div className="checkbox-200px">
            <Checkbox
              id="systemAdmin"
              name="System Admin"
              checked={role === "Admin"}
              onChange={handleRoleChange}
              disabled={formIsDisabled}
            />
            <label htmlFor="systemAdmin">
              <p>System Admin</p>
            </label>
          </div>

        </div>
    
      </div>

      {/* ------------------FORM ERRORS------------------------------------- */}

              <ul style={{ margin: "0 0 0.5em 0", flexDirection: "row", color: "var(--red)" }}>
          {errors.map((oneError, i) => (
            <li key={i} >{oneError}</li>
          ))}
        </ul>      

      {/* ------------------------------------------------------------------ */}

      <div
        className="modal-buttons"
        style={{ justifyContent: "space-between" }}
      >
        {!user.isDeleted ? 
          <Button
            variant="outlined"
            style={{
              color: "var(--red)",
              background: "white",
              borderColor: "var(--red)",
              whiteSpace: "nowrap",
            }}
            onClick={() => {
              confirmDelete();
            }}
          >
            Delete User
          </Button>
          :
          <Button
            variant="outlined"
            style={{
              color: "var(--red)",
              background: "white",
              borderColor: "var(--red)",
              whiteSpace: "nowrap",
            }}
            onClick={() => {
              setShowRestoreConfirmationModal(true);
            }}
          >
            Restore User
          </Button>
        }

        <div style={{ display:'flex', justifyContent:'flex-end', width:'100%' }} >
          <Button
            variant="contained"
            color="secondary"
            style={{ margin: "0 2em 0 0", width: "100px" }}
            onClick={() => closeModal()}
          >
            Cancel
          </Button>
          {
            formIsDisabled ?
            <Button
              variant="contained"
              style={{ width: "100px" }}
              disabled={user.isDeleted}
              onClick={() => setFormIsDisabled(false)}
            >
              Edit
            </Button>
            :
            <Button
              variant="contained"
              style={{ width: "100px" }}
              onClick={onLocalSubmit}
              disabled={isFetching}
            >
              Save
            </Button>
          }
        </div>
      </div>
      {!creatingNewUser && (
        <div className="modal-footer">
          <p style={{ color: "var(--grey-inactive)", margin: "0 0.5em 0 0" }}>
            Created{" "}
          </p>{" "}
          <p style={{ margin: "0 1em 0 0" }}>
            {parseToEasternTime(user.created)}{" "}
          </p>
          <p style={{ color: "var(--grey-inactive)", margin: "0 0.5em 0 0" }}>
            Modified on{" "}
          </p>{" "}
          <p>{parseToEasternTime(user.updated)}</p>
        </div>
      )}
      {/* only show footer if we are editing */}
    </div>
  );
}
